import styles from './Positions.module.scss';

import { Box, IconButton, Theme, useMediaQuery } from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { ColumnInstance, TableInstance } from 'react-table';

import { ReactComponent as ColumnsIcon } from '@work4all/assets/icons/columns.svg';

import { ResizableArea } from '@work4all/components/lib/components/ResizableArea';
import {
  ColumnVisibilityContext,
  useColumnVisibilityManager,
} from '@work4all/components/lib/dataDisplay/basic-table/hooks/useColumnVisibility';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';

import { PriceSettingsProvider } from '@work4all/data';
import { CustomFieldsConfigProvider } from '@work4all/data/lib/custom-fields';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';
import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { EInvoiceType } from '@work4all/models/lib/Enums/EInvoiceType.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { ColumnsVisibility } from '../../../../../../../../../components/data-tables/table/components/toolbar/components/columns-visibility';
import { settings, useSetting } from '../../../../../../../../../settings';
import { useFormContextPlus } from '../../../../../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../../../../../hooks/mask-context';
import { withAutoDisable } from '../../../../../../utils/with-auto-disable';
import { ErpPaymentData } from '../../../../ErpData';
import { ArticleOptions } from '../../../../hooks/use-bz-shadow-object-api';

import { ClonePositionButton } from './clone-position-button/ClonePositionButton';
import { IEditTable } from './edit-table/types';
import { ErpLanguagePicker } from './erp-language-picker/ErpLanguagePicker';
import { useBomDialog } from './hooks/use-bom-dialog';
import { usePositionState } from './hooks/use-position-state';
import { usePositionsColumns } from './hooks/use-positions-columns';
import { SelectedPositionProvider } from './hooks/use-selected-position';
import { MoreActionButtonButton } from './more-actions-button/MoreActionsButton';
import { NewPostitionButton } from './new-position-button/NewPostitionButton';
import { PositionPicker } from './position-picker/PositionPicker';
import { BOMDialog } from './positions-bill-of-materials/BOMDialog';
import { PositionsSettings } from './positions-settings/PositionsSettings';
import {
  ForwardedEditTableProps,
  IPositionsTableProps,
  PositionsTable,
} from './positions-table/PositionsTable';
import { ErpPriceGroupPicker } from './price-group-picker/ErpPriceGroupPicker';
import { RemovePositionButton } from './remove-position-button/RemovePositionButton';
import { RowSizePicker } from './row-size-picker/RowSizePicker';
import { handleOrderChange } from './utils/handle-order-change';

interface IPositionsProps
  extends ForwardedEditTableProps,
    Pick<IPositionsTableProps, 'showPositionMask' | 'disabled' | 'singleLine'> {
  showArticleSelectionBig?: boolean;
}

const emptyColumns: ColumnInstance[] = [];

const PARTIAL_OR_FINAL_INVOICE_TYPES = [
  EInvoiceType.PARTIAL_INVOICE,
  EInvoiceType.PARTIAL_CONSTRUCTION_INVOICE,
  EInvoiceType.PARTIAL_FINAL_CONSTRUCTION_INVOICE,
  EInvoiceType.FINAL_CONSTRUCTION_INVOICE,
];

export const Positions: React.FC<IPositionsProps> = (props) => {
  const {
    allowedColumns,
    disabled = false,
    showArticleSelectionBig = true,
    showPositionMask = true,
    showBorders,
    showSelectionColumn,
    singleLine,
  } = props;

  const editTableRef = useRef<IEditTable>(null);
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const { entity } = useMaskContext();
  const { watch } = useFormContextPlus<ErpPaymentData>();

  const tableInstanceRef = useRef<TableInstance>(null);

  const columnsPopoverState = usePopoverState();
  const openColumnsPopover = columnsPopoverState.handleClick;

  const columnSettings = useSetting(settings.erpPositionsConfig(entity));
  const handleVisibilityChange = useCallback(
    (columns: string[]) => {
      if (!columns.filter((x) => SELECTION_COLUMN_ID !== x).length) return;
      columnSettings.set({
        ...columnSettings.value,
        order: columns,
        visibility: columns,
      });
    },
    [columnSettings]
  );

  const columnsVisibilityManager = useColumnVisibilityManager({
    tableInstanceRef,
    onVisibilityChange: handleVisibilityChange,
    orderColumns: handleOrderChange,
    disabledColumns: ['longtext', 'number'],
  });

  const {
    positions,
    total,
    onAddPosition,
    onClonePosition,
    convertToInternal,
    convertToNormal,
    onRemovePosition,
    onMovePosition,
    onEditPosition,
    onCollapsePosition,
    onAddPartialInvoicePosition,
  } = usePositionState({
    editTableRef,
    entity,
    isSimpleMode: !showBorders,
    disabled,
  });

  const onAddSpecial = useCallback(
    (positionType: ErpPositionsKind, article) => {
      onAddPosition({
        positionType,
        article,
      });
      if (horizontalScrollRef?.current)
        horizontalScrollRef.current.scrollLeft = 0;
    },
    [onAddPosition]
  );

  const previewSize = useSetting(settings.erpArticleSize());
  const erpShowArticle = useSetting(settings.erpShowArticle());

  const { bomArticle, onAddBomArticle, onAddPositionWithArticle, onBomClose } =
    useBomDialog({
      onAddPosition,
      entity,
    });

  const { columns, resetColumns } = usePositionsColumns({
    tableInstanceRef,
    allowedColumns,
    total,
    onAddPosition: onAddPositionWithArticle,
    onEditPosition,
    disabled,
    editTableRef,
    showPositionInfo: true,
  });

  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const onAddOnArticlePicker = useCallback(
    (article: Article, options?: ArticleOptions) => {
      onAddPositionWithArticle({
        positionType: ErpPositionsKind.STANDARD,
        article,
        purchasePrice: options?.purchasePrice,
        update: options?.update,
      });
    },
    [onAddPositionWithArticle]
  );

  const onPickPosition = useCallback(
    (
      data?: Article | Invoice | string,
      position?: { index: number; dropZoneType: 'before' | 'after' | 'inside' }
    ) => {
      // Calculate the actual index based on dropZoneType
      // 'before': insert at index
      // 'after': insert after index (index + 1)
      // 'inside': treat as after for now
      const targetIndex = position
        ? position.dropZoneType === 'before'
          ? position.index
          : position.index + 1
        : undefined;

      if (typeof data === 'string') {
        onAddPosition({
          positionType: ErpPositionsKind.TITEL,
          defaultText: data,
          index: targetIndex,
        });
      } else if (data?.__typename === 'Rechnung') {
        onAddPartialInvoicePosition(data.id);
      } else if (data)
        onAddPositionWithArticle({
          positionType: ErpPositionsKind.STANDARD,
          article: data as Article,
          index: targetIndex,
        });
      else
        onAddPosition({
          positionType: ErpPositionsKind.TEXTZEILE,
          index: targetIndex,
        });
    },
    [onAddPosition, onAddPositionWithArticle, onAddPartialInvoicePosition]
  );

  const businessPartnerContactCombined = watch(
    'businessPartnerContactCombined'
  );

  const customerId =
    businessPartnerContactCombined?.businessPartner?.businessPartnerType ===
    SdObjType.KUNDE
      ? businessPartnerContactCombined?.businessPartner?.id
      : undefined;

  const positionsTable = useMemo(() => {
    return (
      <PositionsTable
        refs={{
          editTable: editTableRef,
          scroll: horizontalScrollRef,
        }}
        columns={columns}
        tableInstanceRef={tableInstanceRef}
        disabled={disabled}
        positions={positions ?? []}
        onMovePosition={onMovePosition}
        onPickPosition={onPickPosition}
        onRemovePosition={onRemovePosition}
        onEditPosition={onEditPosition}
        onCollapsePosition={onCollapsePosition}
        showPositionMask={showPositionMask}
        showBorders={showBorders}
        allowedColumns={allowedColumns}
        onAddPosition={onAddPositionWithArticle}
        showSelectionColumn={showSelectionColumn}
        singleLine={singleLine}
        total={total}
      />
    );
  }, [
    columns,
    disabled,
    positions,
    onAddPositionWithArticle,
    onMovePosition,
    onPickPosition,
    onRemovePosition,
    onEditPosition,
    onCollapsePosition,
    showPositionMask,
    showBorders,
    allowedColumns,
    showSelectionColumn,
    singleLine,
    total,
  ]);

  const frozen = watch('frozen');
  const currency = watch('currency');
  const eInvoiceType = (watch as UseFormWatch<PathsOf<Invoice, 2>>)(
    'eInvoiceType_BT3'
  );
  const erpSize = useSetting(settings.erpRowSize());

  const isPartialOrFinalInvoice =
    PARTIAL_OR_FINAL_INVOICE_TYPES.includes(eInvoiceType);

  return (
    <SelectedPositionProvider>
      <ColumnVisibilityContext.Provider value={columnsVisibilityManager}>
        <div className={styles['tableWrapper']}>
          {!isViewportDownMd && showArticleSelectionBig && (
            <Box
              padding="0.5rem 1.75rem"
              display="flex"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap="0.5rem">
                <NewPostitionButton
                  disabled={disabled}
                  onAddSpecial={onAddSpecial}
                  editTableRef={editTableRef}
                />
                <RemovePositionButton
                  disabled={disabled}
                  onRemovePosition={onRemovePosition}
                />
                <ClonePositionButton
                  disabled={disabled}
                  onClonePosition={onClonePosition}
                  positions={positions}
                />
                <MoreActionButtonButton
                  disabled={disabled}
                  convertToInternal={convertToInternal}
                  convertToNormal={convertToNormal}
                  positions={positions}
                />
              </Box>

              <Box display="flex">
                <ErpLanguagePicker />
                {entity !== Entities.inboundDeliveryNote && (
                  <ErpPriceGroupPicker
                    disabled={frozen === false ? undefined : true}
                  />
                )}
                <Box width="0.5rem" />
                <RowSizePicker value={erpSize.value} onChange={erpSize.set} />
                <IconButton
                  color="primary"
                  onClick={(event) => {
                    return openColumnsPopover(event);
                  }}
                >
                  <ColumnsIcon />
                </IconButton>
                <PositionsSettings />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flex: 1,
              borderBottom: '1px solid var(--ui04)',
            }}
          >
            {positionsTable}
          </Box>

          <ColumnsVisibility
            popoverState={columnsPopoverState}
            visibleColumns={
              tableInstanceRef.current?.allColumns ?? emptyColumns
            }
            onResetColumns={resetColumns}
          />
        </div>
        {!isViewportDownMd && showArticleSelectionBig && (
          <ResizableArea
            handles="top"
            className={styles.resizable}
            direction="vertical"
            size={
              erpShowArticle.value
                ? previewSize.value
                : { height: remToPx(3.25) }
            }
            onResize={(size) => {
              previewSize.set(size);
              erpShowArticle.set(true);
            }}
            maxHeight={1000}
            minHeight={erpShowArticle.value ? remToPx(8.5) : remToPx(3.25)}
          >
            <PriceSettingsProvider>
              <PositionPicker
                disabled={disabled}
                className={styles.picker}
                onAddStandard={onAddOnArticlePicker}
                onAddPartialInvoicePosition={onAddPartialInvoicePosition}
                onGroupDoubleClick={onPickPosition}
                customerId={customerId}
                currency={currency}
                showPartialInvoicesTab={
                  entity === Entities.invoice && isPartialOrFinalInvoice
                }
              />
            </PriceSettingsProvider>
          </ResizableArea>
        )}
        <BOMDialog
          entity={entity}
          article={bomArticle}
          onAddArticle={onAddBomArticle}
          onClose={onBomClose}
        />
      </ColumnVisibilityContext.Provider>
    </SelectedPositionProvider>
  );
};

const PositionsWithCustomColumns = (props: IPositionsProps) => {
  return (
    <CustomFieldsConfigProvider entity={Entities.position}>
      <Positions {...props} />
    </CustomFieldsConfigProvider>
  );
};

export const PositionsWithAutoDisable = withAutoDisable(
  React.memo(PositionsWithCustomColumns)
);
